import React, { useCallback, useMemo } from 'react'
import { Label } from 'reactstrap'
import { CheckBox } from '@/components/CheckBox'
import { useEnumItems } from '@/hooks/useEnumItems'
import { individualPermissionTypes } from '@/enums/individualPermissionTypes'
import Resx from '@/components/Resx'
import { TabNavItems, TabContent, TabPane } from '@/components/tabs'
import { institutionPermissionTypes } from '@/enums/institutionPermissionTypes'
import PropTypes from 'prop-types'

const InstitutionPermissionCheckBoxes = React.memo(({ individualReadOnly,
  onIndividualReadOnlyChange,
  individualEdit,
  onIndividualEditChange,
  institutionReadOnly,
  onInstitutionReadOnlyChange,
  institutionEdit,
  onInstitutionEditChange }) => {
  const individualItems = useEnumItems(individualPermissionTypes).filter(i => i.value !== 0)
  const institutionItems = useEnumItems(institutionPermissionTypes).filter(i => i.value !== 0)
  const { bankPermissions, companyPermissions, sharedPermissions } = institutionPermissionTypes

  const bankItems = useMemo(() => institutionItems.filter(i => bankPermissions.includes(i.key)),
    [bankPermissions, institutionItems])

  const companyItems = useMemo(() => institutionItems.filter(i => companyPermissions.includes(i.key)),
    [companyPermissions, institutionItems])

  const sharedItems = useMemo(() => institutionItems.filter(i => sharedPermissions.includes(i.key)),
    [institutionItems, sharedPermissions])

  const readOnlyPermissions = useMemo(() => ['InstitutionGroups',
    'IndividualsInCharge',
    'AssociatedToInstitutions',
    'StatusHistory',
    'Blacklists',
    'ChangeHistory',
    'BankDealsHistory',
    'IndividualBlacklists',
    'IndividualChangeHistory',
    'IndividualCbsReport'], [])

  const [activeTab, setActiveTab] = React.useState('1')

  // if the onChange methods for institution permissions are not provided, we don't show their tab
  const showInstitutionPermissions = useMemo(() => onInstitutionEditChange || onInstitutionReadOnlyChange, [onInstitutionEditChange, onInstitutionReadOnlyChange])

  return (
    <React.Fragment>
      <TabNavItems
        buttonView
        activeTabId={activeTab}
        onClick={setActiveTab}
        items={[
          { id: '1', text: 'Common.Individuals' },
          { id: '2', text: 'Common.Banks' },
          { id: '3', text: 'Common.Companies' }
        ].filter(item => item.id === '1' || showInstitutionPermissions)} />
      <TabContent activeTab={activeTab} renderActiveOnly>
        <TabPane tabId='1'>
          <CheckboxTable permissionList={individualItems}
            readOnlyPermissions={readOnlyPermissions}
            readPermissions={individualReadOnly}
            writePermissions={individualEdit}
            onReadChange={onIndividualReadOnlyChange}
            onWriteChange={onIndividualEditChange} />
        </TabPane>
        <TabPane tabId='2'>
          <CheckboxTable permissionList={bankItems}
            sharedPermissionList={sharedItems}
            readOnlyPermissions={readOnlyPermissions}
            readPermissions={institutionReadOnly}
            writePermissions={institutionEdit}
            onReadChange={onInstitutionReadOnlyChange}
            onWriteChange={onInstitutionEditChange} />
        </TabPane>
        <TabPane tabId='3'>
          <CheckboxTable permissionList={companyItems}
            sharedPermissionList={sharedItems}
            readOnlyPermissions={readOnlyPermissions}
            readPermissions={institutionReadOnly}
            writePermissions={institutionEdit}
            onReadChange={onInstitutionReadOnlyChange}
            onWriteChange={onInstitutionEditChange} />
        </TabPane>
      </TabContent>
    </React.Fragment>
  )
})

InstitutionPermissionCheckBoxes.propTypes = {
  individualReadOnly: PropTypes.number.isRequired,
  onIndividualReadOnlyChange: PropTypes.func.isRequired,
  individualEdit: PropTypes.number.isRequired,
  onIndividualEditChange: PropTypes.func.isRequired,
  institutionReadOnly: PropTypes.number.isRequired,
  onInstitutionReadOnlyChange: PropTypes.func,
  institutionEdit: PropTypes.number.isRequired,
  onInstitutionEditChange: PropTypes.func
}

const CheckboxTable = React.memo(({ permissionList,
  sharedPermissionList,
  readOnlyPermissions,
  readPermissions,
  writePermissions,
  onReadChange,
  onWriteChange }) => {
  const combinedPermissions = useMemo(() => [ ...permissionList, ...(sharedPermissionList || []) ],
    [permissionList, sharedPermissionList])

  const allPermissions = useMemo(() => combinedPermissions.map(i => i.value).reduce((a, b) => a | b, 0),
    [combinedPermissions])
  const allWritePermissions = useMemo(() =>
    combinedPermissions.filter(i => !readOnlyPermissions.includes(i.key)).map(i => i.value).reduce((a, b) => a | b, 0),
  [combinedPermissions, readOnlyPermissions])

  const handleReadChange = useCallback((checked, value) => {
    onReadChange(checked ? readPermissions | value : readPermissions & ~value)
  }, [onReadChange, readPermissions])

  const handleWriteChange = useCallback((checked, value) => {
    onWriteChange(checked ? writePermissions | value : writePermissions & ~value)
    if (checked) {
      onReadChange(readPermissions | value)
    }
  }, [onReadChange, onWriteChange, readPermissions, writePermissions])

  const handleAllRead = useCallback((checked, value) => {
    if (!checked) {
      onReadChange(writePermissions)
    } else {
      onReadChange(value)
    }
  }, [onReadChange, writePermissions])

  return <div className='overflow-auto'>
    <table className='table table-sm table-striped'>
      <thead>
        <tr>
          <td />
          <td>
            <Label>
              <Resx name='Common.Read' />
            </Label>
          </td>
          <td>
            <Label>
              <Resx name='Common.Write' />
            </Label>
          </td>
        </tr>
      </thead>
      <tbody>
        <tr>
          <th>
            <Label>
              <Resx name='Common.All' />
            </Label>
          </th>
          <th>
            <CheckBox id='permissionRead_all' value={allPermissions} checked={(readPermissions & allPermissions) === allPermissions} onChange={handleAllRead} />
          </th>
          <th>
            <CheckBox id='permissionWrite_all' value={allWritePermissions} checked={(writePermissions & allWritePermissions) === allWritePermissions} onChange={handleWriteChange} />
          </th>
        </tr>
        {permissionList.map(i =>
          <tr key={i.key}>
            <th>
              <Label>
                <Resx name={i.text} />
              </Label>
            </th>
            <th>
              <CheckBox id={`permissionRead_${i.value}`} disabled={i.value & writePermissions} value={i.value} checked={(readPermissions & i.value) === i.value} onChange={handleReadChange} />
            </th>
            <th>
              {!readOnlyPermissions.includes(i.key) && <CheckBox id={`permissionWrite_${i.value}`} value={i.value} checked={(writePermissions & i.value) === i.value} onChange={handleWriteChange} />}
            </th>
          </tr>)}
        {sharedPermissionList && <tr><td colSpan='3'><h3><Resx name='Common.SharedPermissions' /></h3></td></tr>}
        {sharedPermissionList && sharedPermissionList.map(i =>
          <tr key={i.key}>
            <th>
              <Label>
                <Resx name={i.text} />
              </Label>
            </th>
            <th>
              <CheckBox id={`permissionRead_${i.value}`} disabled={i.value & writePermissions} value={i.value} checked={(readPermissions & i.value) === i.value} onChange={handleReadChange} />
            </th>
            <th>
              {!readOnlyPermissions.includes(i.key) && <CheckBox id={`permissionWrite_${i.value}`} value={i.value} checked={(writePermissions & i.value) === i.value} onChange={handleWriteChange} />}
            </th>
          </tr>)}
      </tbody>
    </table>
  </div>
})

CheckboxTable.propTypes = {
  permissionList: PropTypes.array.isRequired,
  sharedPermissionList: PropTypes.array,
  readOnlyPermissions: PropTypes.array.isRequired,
  readPermissions: PropTypes.number.isRequired,
  writePermissions: PropTypes.number.isRequired,
  onReadChange: PropTypes.func.isRequired,
  onWriteChange: PropTypes.func.isRequired
}

export { InstitutionPermissionCheckBoxes }



// WEBPACK FOOTER //
// ./src/dialogs/institutionPermission/institutionPermissionCheckBoxes.js